/**
 * デバイス Drawer の「Android」タブ。AMAPI で管理している端末を操作する。
 *
 * **Apple タブとの違い**(見え方に直結する):
 *
 * - AMAPI は**その場で受け付ける**ので、押した直後は `pushed`(Google が受けた)。
 *   端末に効いたかは分からない
 * - 決着は 5 分ごとの同期が **Operation**(`devices.operations.get`)を見て付ける。
 *   端末が状態を報告したことでは決着しない(中 4)。だから Apple の 5 秒より
 *   緩い 10 秒ごとに見に行く
 * - 初期化は `DELETE`(= 端末を初期化して企業から外す)。**戻せない**
 * - 応答を失った行は `pushed` のまま `response.delivered="unknown"` になる。
 *   **「渡した」とは言わず「届いたか不明」として別に出す**(高 3)。効いている
 *   かもしれないので、同じ破壊操作の送り直しには別の確認が要る
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";

import type { DeviceDetail, MdmCommand } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import {
  errorText,
  useAndroidDeviceCommands,
  useInvalidateMdm,
  useMdmPerms,
} from "./hooks";

const STATUS: Record<string, { color: string; label: string }> = {
  queued: { color: "default", label: "積んだ" },
  pushed: { color: "blue", label: "Google が受けた" },
  push_failed: { color: "red", label: "渡せなかった" },
  acknowledged: { color: "green", label: "端末で効いた(Operation)" },
  // 初期化の決着。「受け取った」ではなく**企業から消えた**こと(中 4)
  removed: { color: "green", label: "管理から外れた" },
  error: { color: "red", label: "端末で失敗した" },
};

/** サーバーが `response.delivered` に入れる印(届いたかが分からない行)。 */
const DELIVERY_UNKNOWN = "unknown";
/** 送り直すときに付ける明示確認(サーバーの `RESEND_CONFIRM` と同じ値)。 */
const RESEND_CONFIRM = "resend";
const UNKNOWN_HINT =
  "Google に届いたかが分からない(効いているかもしれない)。" +
  "同期が Operation を確かめるか、人が端末を見るまで決着しない";

/** まだ決着していない status。ここに居る間だけ「届いたか不明」を出す。 */
const UNSETTLED = new Set(["queued", "pushed"]);

/** 届いたかが分からない行か。**`push_failed` 以外を「届いた」と読まない**(高 3)。
 *
 * `response.delivered` の印は決着しても消えない(`resolve_gone()` は status を
 * 書き換えるだけ)ので、**未決着の行だけ**に出す(自己レビュー 11)。初期化が
 * `removed` まで行った行に「届いたか不明」と出ると、人は何を見ればいいか分からない。
 */
function isUnknown(row: MdmCommand): boolean {
  if (!UNSETTLED.has(row.status)) return false;
  const response = (row.response ?? {}) as { delivered?: unknown };
  return String(response.delivered ?? "") === DELIVERY_UNKNOWN;
}

/** 届いたか分からないまま、端末が AMAPI の一覧から消えた行か(fix2 高 3)。
 *
 * サーバーは一覧から消えたことを `response.device_gone_at` に控えるだけで
 * **決着させない**(消えた理由がこちらの操作とは限らない。手で初期化された /
 * 企業から外された、かもしれない)。画面も「終わった」と読ませず、
 * 「管理から消えた」という観測だけを足して出す。送り直しの承知も要り続ける。
 */
function isGone(row: MdmCommand): boolean {
  if (!isUnknown(row)) return false;
  const response = (row.response ?? {}) as { device_gone_at?: unknown };
  return Boolean(response.device_gone_at);
}

/** 「届いたか不明」の札の文字。消えた観測があるときだけそれを足す。 */
function unknownLabel(row: MdmCommand): string {
  return isGone(row) ? "端末が管理から消えた(届いたかは不明)" : "届いたか不明";
}

function unknownHint(row: MdmCommand): string {
  if (!isGone(row)) return UNKNOWN_HINT;
  return (
    "端末が管理から消えた(届いたかは不明)。" +
    "消えた理由はこの操作とは限らない(手で初期化された / 企業から外された、" +
    "かもしれない)ので決着させていない。端末を見て確かめた人が決着させる"
  );
}

/** 人が端末を見て決着させた行の札(fix3 中 3)。
 *
 * `acknowledged` に決着させた行を「端末で効いた(Operation)」と出すと、
 * 端末からの Operation が返ってきたように読める(実際は人の確認)。
 * `removed` も同じで、同期が見たのか人が決めたのかを区別して出す。
 */
const SETTLED_LABEL: Record<string, string> = {
  removed: "管理者が確認した(管理から外れた)",
  acknowledged: "管理者が確認した(効いていた)",
  error: "管理者が確認した(効いていない)",
};

/** 管理者が決着させた行か(サーバーが `response.resolved_by` に残す)。 */
function settledByAdmin(row: MdmCommand): boolean {
  const response = (row.response ?? {}) as { resolved_by?: unknown };
  return String(response.resolved_by ?? "") === "admin";
}

/** 決着のときに残した覚え書き(誰が・どうやって確かめたか)。 */
function settleNote(row: MdmCommand): string {
  const response = (row.response ?? {}) as {
    settled_by?: unknown;
    settled_note?: unknown;
    send_error?: unknown;
  };
  const parts = [
    `確かめた人: ${String(response.settled_by ?? "-")}`,
    `覚え書き: ${String(response.settled_note ?? "") || "-"}`,
  ];
  // 決着前の送信エラーは履歴として応答に退避してある(fix3 中 3)
  if (response.send_error) parts.push(`決着前: ${String(response.send_error)}`);
  return parts.join(" / ");
}

const TYPE_LABEL: Record<string, string> = {
  LOCK: "ロック",
  REBOOT: "再起動",
  RESET_PASSWORD: "パスワード再設定",
  RELINQUISH_OWNERSHIP: "所有権の返却",
  WIPE: "初期化",
};

/** 端末の状態(AMAPI の `Device.state`)。 */
const STATE: Record<string, { color: string; label: string }> = {
  ACTIVE: { color: "green", label: "稼働中" },
  DISABLED: { color: "orange", label: "停止中" },
  PROVISIONING: { color: "blue", label: "登録中" },
  DELETED: { color: "red", label: "管理から外れた" },
};

const RESET_FLAGS = [
  { value: "REQUIRE_ENTRY", label: "REQUIRE_ENTRY(次回に必ず入力させる)" },
  {
    value: "DO_NOT_ASK_CREDENTIALS_ON_BOOT",
    label: "DO_NOT_ASK_CREDENTIALS_ON_BOOT(起動時に聞かない)",
  },
  { value: "LOCK_NOW", label: "LOCK_NOW(今すぐロック)" },
];

const WIPE_FLAGS = [
  {
    value: "PRESERVE_RESET_PROTECTION_DATA",
    label: "PRESERVE_RESET_PROTECTION_DATA(初期化保護を残す)",
  },
  { value: "WIPE_EXTERNAL_STORAGE", label: "WIPE_EXTERNAL_STORAGE(SD カードも消す)" },
];

/** ロック系の旗が付いた再設定は、製造番号の打ち直しが要る(サーバーと同じ判定)。 */
const LOCK_FLAGS = ["DO_NOT_ASK_CREDENTIALS_ON_BOOT", "LOCK_NOW"];

function statusTag(row: MdmCommand) {
  if (isUnknown(row)) {
    // `pushed` のままだが「渡した」ではない。**別の札**にして取り違えを防ぐ
    return (
      <Tooltip title={unknownHint(row)}>
        <Tag color="orange">{unknownLabel(row)}</Tag>
      </Tooltip>
    );
  }
  const s = STATUS[row.status] ?? { color: "default", label: row.status };
  if (settledByAdmin(row)) {
    // **人の確認**であることを札に出す(fix3 中 3)
    return (
      <Tooltip title={settleNote(row)}>
        <Tag color={s.color}>{SETTLED_LABEL[row.status] ?? s.label}</Tag>
      </Tooltip>
    );
  }
  return <Tag color={s.color}>{s.label}</Tag>;
}

function ResponseView({ row }: { row: MdmCommand }) {
  const body = Object.keys(row.response ?? {}).length ? row.response : null;
  const payload = Object.keys(row.payload ?? {}).length ? row.payload : null;
  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      {isUnknown(row) && (
        <Alert
          type="warning"
          showIcon
          message={unknownLabel(row)}
          description={unknownHint(row)}
        />
      )}
      {settledByAdmin(row) && (
        <Alert
          type={row.status === "error" ? "warning" : "info"}
          showIcon
          message={SETTLED_LABEL[row.status] ?? "管理者が決着させた"}
          description={settleNote(row)}
        />
      )}
      {/* 人が決着させた行の `error` は決着の印(決着前の送信エラーは上の Alert の
          「決着前」に出る)。赤で重ねると古い送信エラーが残って見える(fix4 低 1) */}
      {row.error && !isUnknown(row) && !settledByAdmin(row) && (
        <Alert type="error" showIcon message={row.error} />
      )}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        CommandUUID: {row.command_uuid}
      </Typography.Text>
      <pre style={{ margin: 0, maxHeight: 240, overflow: "auto", fontSize: 12 }}>
        {JSON.stringify({ payload, response: body }, null, 2)}
      </pre>
    </Space>
  );
}

type Pending = "RESET_PASSWORD" | "RELINQUISH_OWNERSHIP" | "WIPE";

export function AndroidTab({ device, open }: { device: DeviceDetail; open: boolean }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const { canWrite, canAdmin } = useMdmPerms();
  const query = useAndroidDeviceCommands(device.id, open);
  const [pending, setPending] = useState<Pending | null>(null);
  const [form] = Form.useForm();
  const flags: string[] = Form.useWatch("flags", form) ?? [];

  const link = query.data?.device ?? null;
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const gone = link?.state === "DELETED";

  // 確認文に打たせる文字(中 10)。製造番号が第一だが、私用ありの仕事用プロファイルは
  // `serialNumber` を返さないので、`device_name` の末尾(AMAPI の端末 ID)を使う
  const confirmWord =
    (device.serial || link?.device_name.split("/").pop() || "").trim();
  const confirmLabel = device.serial ? "製造番号" : "端末 ID";

  // 届いたか分からないまま残っている破壊操作の種類。送り直しに確認を足す(高 3)
  const unknownTypes = new Set(rows.filter(isUnknown).map((r) => r.request_type));
  const needResend = pending !== null && unknownTypes.has(pending);

  const done = (label: string) => {
    void message.success(`${label}を Google に渡した(効いたかは次の同期で分かる)`);
    setPending(null);
    form.resetFields();
    invalidate();
    void query.refetch();
  };

  /** 202 で返った行の読み方。**`push_failed` 以外を「渡した」と読まない**(高 3)。 */
  const settled = (row: MdmCommand, label: string) => {
    if (row.status === "push_failed") {
      void message.warning(`Google に渡せなかった: ${row.error}`);
      setPending(null);
      form.resetFields();
      void query.refetch();
      return;
    }
    if (isUnknown(row)) {
      void message.warning(`${label}は Google に届いたか分からない。${unknownHint(row)}`);
      setPending(null);
      form.resetFields();
      invalidate();
      void query.refetch();
      return;
    }
    done(label);
  };

  const send = useMutation({
    mutationFn: (body: {
      type: string;
      params?: Record<string, unknown>;
      confirm?: string;
    }) => api.androidCommand(device.id, body),
    onSuccess: (row) => settled(row, TYPE_LABEL[row.request_type] ?? row.request_type),
    onError: (e) => void message.error(errorText(e)),
  });

  const wipe = useMutation({
    mutationFn: (body: { confirm: string; wipe_data_flags?: string[] }) =>
      api.wipeAndroidDevice(device.id, body),
    // 初期化も失敗しても 202 + push_failed で返る(送信と同じ)。
    // 「渡した」と言い切らず、行の状態で出し分ける
    onSuccess: (row) => settled(row, "初期化"),
    onError: (e) => void message.error(errorText(e)),
  });

  /** 届いたか不明のまま送り直すときの確認(サーバーの `resend` に対応)。 */
  const resendField = needResend ? (
    <Form.Item
      name="resend"
      valuePropName="checked"
      rules={[
        {
          validator: (_, value) =>
            value
              ? Promise.resolve()
              : Promise.reject(new Error("届いたか不明なので、承知のチェックが要る")),
        },
      ]}
    >
      <Checkbox data-testid="android-resend">
        届いたか不明のまま送り直す(二重に効く可能性を承知している)
      </Checkbox>
    </Form.Item>
  ) : null;

  const columns: ColumnsType<MdmCommand> = [
    {
      title: "種類",
      dataIndex: "request_type",
      width: 170,
      render: (v: string) => TYPE_LABEL[v] ?? v,
    },
    {
      title: "状態",
      dataIndex: "status",
      width: 170,
      render: (_: string, row: MdmCommand) => statusTag(row),
    },
    { title: "実行者", dataIndex: "created_by", width: 160, },
    {
      title: "送信",
      dataIndex: "created_at",
      width: 180,
      render: (v: string | null) => jst(v),
    },
    {
      title: "更新",
      dataIndex: "updated_at",
      width: 180,
      render: (v: string | null) => jst(v),
    },
  ];

  if (!link) {
    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message="この端末は Android 管理に紐付いていない"
          description="「Android」画面で登録トークンを発行し、QR を端末に読ませると 5 分以内の同期でここに出る。"
        />
        {query.isError && <Alert type="error" showIcon message={errorText(query.error)} />}
      </Space>
    );
  }

  const state = STATE[link.state] ?? { color: "default", label: link.state || "—" };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Descriptions size="small" column={1} bordered data-testid="android-link">
        <Descriptions.Item label="AMAPI の端末">{link.device_name}</Descriptions.Item>
        <Descriptions.Item label="状態">
          <Tag color={state.color}>{state.label}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="登録トークン">
          {link.enrollment_token_name || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="最後の同期">{jst(link.last_sync_at)}</Descriptions.Item>
      </Descriptions>

      {gone && (
        <Alert
          type="warning"
          showIcon
          message="この端末はもう管理していない(初期化済み、または所有権を返した)"
          description="台帳の行は残すが、コマンドは送れない。"
        />
      )}

      {unknownTypes.size > 0 && (
        <Alert
          type="warning"
          showIcon
          data-testid="android-unknown"
          message="届いたか分からないコマンドが残っている"
          description={`${[...unknownTypes]
            .map((t) => TYPE_LABEL[t] ?? t)
            .join(" / ")}。${UNKNOWN_HINT}。同じ操作を送り直すには承知のチェックが要る。`}
        />
      )}

      {canWrite && !gone && (
        <Card size="small" title="この端末に送る">
          <Space wrap>
            <Popconfirm
              title="この端末をロックする"
              description="画面が即座にロックされる(解除は端末のパスワード)"
              okText="ロックする"
              cancelText="やめる"
              onConfirm={() => send.mutate({ type: "LOCK" })}
            >
              <Button loading={send.isPending}>ロック</Button>
            </Popconfirm>
            <Popconfirm
              title="この端末を再起動する"
              description="作業中のものは保存されない"
              okText="再起動する"
              cancelText="やめる"
              onConfirm={() => send.mutate({ type: "REBOOT" })}
            >
              <Button danger loading={send.isPending}>
                再起動
              </Button>
            </Popconfirm>
            {canAdmin && (
              <>
                <Button danger onClick={() => setPending("RESET_PASSWORD")}>
                  パスワード再設定
                </Button>
                <Button danger onClick={() => setPending("RELINQUISH_OWNERSHIP")}>
                  所有権の返却
                </Button>
                <Button
                  danger
                  disabled={!confirmWord}
                  title={
                    confirmWord
                      ? undefined
                      : "製造番号も端末 ID も分からない端末は初期化できない(同期を待つ)"
                  }
                  onClick={() => setPending("WIPE")}
                >
                  初期化
                </Button>
              </>
            )}
          </Space>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            Google が受けた時点で「渡した」になる。端末に効いたかは次の同期
            (5 分ごと)で「完了」に変わって分かる。
          </Typography.Paragraph>
        </Card>
      )}

      <Card size="small" title={`コマンド履歴(${total} 件)`} data-testid="android-commands">
        <Table<MdmCommand>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={rows}
          loading={query.isFetching && !query.data}
          pagination={false}
          scroll={{ x: "max-content", y: 320 }}
          expandable={{ expandedRowRender: (row) => <ResponseView row={row} /> }}
          locale={{ emptyText: "まだ何も送っていない" }}
        />
        {total > rows.length && (
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}
          >
            {`新しい順に ${rows.length} 件だけ表示(全 ${total} 件)`}
          </Typography.Paragraph>
        )}
      </Card>

      <Modal
        open={pending === "RESET_PASSWORD"}
        title="端末のパスワードを再設定する"
        okText="再設定する"
        okButtonProps={{ danger: true }}
        cancelText="やめる"
        confirmLoading={send.isPending}
        onCancel={() => {
          setPending(null);
          form.resetFields();
        }}
        onOk={() =>
          void form.validateFields().then((v) =>
            send.mutate({
              type: "RESET_PASSWORD",
              params: {
                ...(v.new_password ? { new_password: v.new_password } : {}),
                ...(v.flags?.length ? { flags: v.flags } : {}),
              },
              confirm: v.confirm,
              ...(v.resend ? { resend: RESEND_CONFIRM } : {}),
            }),
          )
        }
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="新しいパスワードはここにしか出ない(サーバーには残さない)"
          description="空のまま送ると、端末のパスワードを「解除」する。利用者に渡す手段を決めてから実行する。"
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="new_password"
            label="新しいパスワード(空は解除)"
            rules={[{ max: 100, message: "100 文字まで" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="flags" label="旗(任意)">
            <Checkbox.Group options={RESET_FLAGS} style={{ display: "grid", rowGap: 4 }} />
          </Form.Item>
          {flags.some((f) => LOCK_FLAGS.includes(f)) && (
            <Form.Item
              name="confirm"
              label={`ロック系の旗が付いている。確認のため${confirmLabel}(${confirmWord})を入力`}
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    String(value ?? "").trim() === confirmWord
                      ? Promise.resolve()
                      : Promise.reject(new Error(`${confirmLabel}が一致しない`)),
                },
              ]}
            >
              <Input placeholder={confirmWord} />
            </Form.Item>
          )}
          {resendField}
        </Form>
      </Modal>

      <Modal
        open={pending === "RELINQUISH_OWNERSHIP"}
        title="この端末の所有権を返す"
        okText="返す"
        okButtonProps={{ danger: true }}
        cancelText="やめる"
        confirmLoading={send.isPending}
        onCancel={() => {
          setPending(null);
          form.resetFields();
        }}
        onOk={() =>
          void form
            .validateFields()
            .then((v) =>
              send.mutate({
                type: "RELINQUISH_OWNERSHIP",
                confirm: v.confirm,
                ...(v.resend ? { resend: RESEND_CONFIRM } : {}),
              }),
            )
        }
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="仕事用プロファイルを消して、端末を利用者の私物に戻す"
          description="戻せない。もう一度管理するには QR から登録し直す。"
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="confirm"
            label={`確認のため${confirmLabel}(${confirmWord})を入力`}
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  String(value ?? "").trim() === confirmWord
                    ? Promise.resolve()
                    : Promise.reject(new Error(`${confirmLabel}が一致しない`)),
              },
            ]}
          >
            <Input placeholder={confirmWord} />
          </Form.Item>
          {resendField}
        </Form>
      </Modal>

      <Modal
        open={pending === "WIPE"}
        title="この端末を初期化する"
        okText="初期化する"
        okButtonProps={{ danger: true }}
        cancelText="やめる"
        confirmLoading={wipe.isPending}
        onCancel={() => {
          setPending(null);
          form.resetFields();
        }}
        onOk={() =>
          void form.validateFields().then((v) =>
            wipe.mutate({
              confirm: v.confirm,
              ...(v.wipe_data_flags?.length ? { wipe_data_flags: v.wipe_data_flags } : {}),
              ...(v.resend ? { resend: RESEND_CONFIRM } : {}),
            }),
          )
        }
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="端末の中身は戻らない。管理からも外れる"
          description={`対象: ${link.device_name}${
            device.serial ? ` / 製造番号 ${device.serial}` : " / 製造番号なし"
          }`}
        />
        <Form form={form} layout="vertical">
          <Form.Item name="wipe_data_flags" label="旗(任意)">
            <Checkbox.Group options={WIPE_FLAGS} style={{ display: "grid", rowGap: 4 }} />
          </Form.Item>
          <Form.Item
            name="confirm"
            label={`確認のため${confirmLabel}(${confirmWord})を入力`}
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  String(value ?? "").trim() === confirmWord
                    ? Promise.resolve()
                    : Promise.reject(new Error(`${confirmLabel}が一致しない`)),
              },
            ]}
          >
            <Input placeholder={confirmWord} />
          </Form.Item>
          {resendField}
        </Form>
      </Modal>
    </Space>
  );
}
